import { useMemo } from "react";
import { useIntl } from "react-intl";
import format from "date-fns/format";
import messages from "./messages";
import { Translations } from "./types";
import { DashboardAppointmentAIReport } from "../AppointmentAIReportsTableContainer/getAppointmentAIReports";
import { formatStudyNameWithSide } from "../../AppointmentOffering/utils/formatStudyNameWithSide";
import { getFullName } from "../../../common/getFullName";

const useTranslations = (params?: {
  mode?: "review" | "edit";
  status?: string;
  report?: DashboardAppointmentAIReport | null;
}): { translations: Translations } => {
  const { formatMessage } = useIntl();
  const { mode, status, report } = params || {};

  const translations = useMemo<Translations>(() => {
    const isReviewMode = mode === "review" || status === "requires_review";

    let customerName: string | undefined;
    let subtitle: string | undefined;

    if (report?.appointment) {
      const { appointment } = report;
      const patientDetails = appointment.appointment_patient_details?.find(
        (d) => d.patient,
      );
      const timeSlot = appointment.time_slot;
      const { center } = appointment;

      if (patientDetails) {
        customerName = getFullName({
          firstName: patientDetails.first_name,
          lastName: patientDetails.last_name,
        });
      }

      if (patientDetails && timeSlot && center) {
        const formattedDate = format(
          new Date(timeSlot.started_at),
          "MM/dd/yyyy",
        );

        const offeringStudyNames =
          appointment.appointment_offerings?.map((o) =>
            formatStudyNameWithSide(o.center_offering.name, o.side),
          ) || [];

        const studyNames = [...offeringStudyNames].join(", ");

        subtitle = formatMessage(messages.subtitle, {
          date: formattedDate,
          locationCity: center.city,
          studyNames,
        });
      }
    }

    const title = isReviewMode
      ? formatMessage(messages.titleReview, {
          customerName: customerName || "",
        })
      : formatMessage(messages.title, { customerName: customerName || "" });

    const saveButtonLabel = isReviewMode
      ? formatMessage(messages.btnApprove)
      : formatMessage(messages.btnSave);

    return {
      title,
      subtitle,
      labelSummary: formatMessage(messages.labelSummary),
      labelFindings: formatMessage(messages.labelFindings),
      labelFindingSeverity: formatMessage(messages.labelFindingSeverity),
      labelFindingTitle: formatMessage(messages.labelFindingTitle),
      labelFindingWhatThisMeans: formatMessage(
        messages.labelFindingWhatThisMeans,
      ),
      labelFindingWhyItMatters: formatMessage(
        messages.labelFindingWhyItMatters,
      ),
      severityActionRequired: formatMessage(messages.severityActionRequired),
      severityMnoActionRequired: formatMessage(
        messages.severityMnoActionRequired,
      ),
      severityRequiresAttention: formatMessage(
        messages.severityRequiresAttention,
      ),
      labelNextSteps: formatMessage(messages.labelNextSteps),
      labelNextStepTitle: formatMessage(messages.labelNextStepTitle),
      labelNextStepDescription: formatMessage(
        messages.labelNextStepDescription,
      ),
      btnSave: saveButtonLabel,
      btnCancel: formatMessage(messages.btnCancel),
      btnAddFinding: formatMessage(messages.btnAddFinding),
      btnAddNextStep: formatMessage(messages.btnAddNextStep),
      btnRemove: formatMessage(messages.btnRemove),
      btnDelete: formatMessage(messages.btnDelete),
      confirmDelete: formatMessage(messages.confirmDelete),
      errorSeverityRequired: formatMessage(messages.errorSeverityRequired),
      errorTitleRequired: formatMessage(messages.errorTitleRequired),
      errorWhatThisMeansRequired: formatMessage(
        messages.errorWhatThisMeansRequired,
      ),
      errorWhyItMattersRequired: formatMessage(
        messages.errorWhyItMattersRequired,
      ),
      errorDescriptionRequired: formatMessage(
        messages.errorDescriptionRequired,
      ),
    };
  }, [formatMessage, mode, status, report]);

  return { translations };
};

export default useTranslations;
